import * as React from 'react';
import { useSelector } from 'react-redux';
import Select from 'antd/lib/select';
import Spin from 'antd/lib/spin';
import { getIssues } from '@/api';
import { IssueEntity, IssueSourceType, IssueState } from '@/types';
import { RootState } from '@/reducers/reducers';
import { selectAuthCredentials } from '@/selectors/selectors';
import IntegrationIcon from '@/components/icons/IntegrationIcon';
import runwayLogo from '../../../assets/integration-runway.svg';

const PAGE_SIZE = 50;

export function useAbsorbableSources(orgId: string, appId: string, open: boolean, excludeIssueId?: string) {
    const auth = useSelector((state: RootState) => selectAuthCredentials(state));
    const [candidates, setCandidates] = React.useState<IssueEntity[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isLoadingMore, setIsLoadingMore] = React.useState(false);
    const [hasMore, setHasMore] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const offsetRef = React.useRef(0);
    const loadingRef = React.useRef(false);

    const fetchPage = React.useCallback(
        async (reset: boolean) => {
            if (loadingRef.current) {
                return;
            }
            loadingRef.current = true;
            const offset = reset ? 0 : offsetRef.current;
            if (reset) {
                setIsLoading(true);
            } else {
                setIsLoadingMore(true);
            }
            try {
                const res = await getIssues(
                    { orgId, appId, statuses: [IssueState.New], search: search || undefined },
                    PAGE_SIZE,
                    offset,
                    auth,
                );
                const page = res.issues.filter((i) => i.id !== excludeIssueId && (i.sources?.length ?? 0) === 1);
                setCandidates((prev) => (reset ? page : [...prev, ...page]));
                offsetRef.current = offset + res.issues.length;
                // A full raw page (before the mono-source filter) means there may be more to load.
                setHasMore(res.issues.length === PAGE_SIZE);
            } catch {
                if (reset) {
                    setCandidates([]);
                }
                setHasMore(false);
            } finally {
                loadingRef.current = false;
                setIsLoading(false);
                setIsLoadingMore(false);
            }
        },
        [orgId, appId, excludeIssueId, auth, search],
    );

    React.useEffect(() => {
        if (!open) {
            return;
        }
        offsetRef.current = 0;
        setCandidates([]);
        fetchPage(true);
    }, [open, fetchPage]);

    const loadMore = () => {
        if (hasMore && !loadingRef.current) {
            fetchPage(false);
        }
    };

    return { candidates, isLoading, isLoadingMore, hasMore, loadMore, setSearch };
}

interface AbsorbableSourcesSelectProps {
    orgId: string;
    appId: string;
    open: boolean;
    excludeIssueId?: string;
    value: string[];
    onChange: (issueIds: string[]) => void;
}

export const AbsorbableSourcesSelect: React.FC<AbsorbableSourcesSelectProps> = ({
    orgId,
    appId,
    open,
    excludeIssueId,
    value,
    onChange,
}) => {
    const { candidates, isLoading, isLoadingMore, hasMore, loadMore, setSearch } = useAbsorbableSources(
        orgId,
        appId,
        open,
        excludeIssueId,
    );
    // Debounce the server-side search so we don't fire a request per keystroke.
    const searchTimer = React.useRef<ReturnType<typeof setTimeout>>();
    React.useEffect(() => () => clearTimeout(searchTimer.current), []);
    const onSearch = (input: string) => {
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => setSearch(input.trim()), 300);
    };
    const onPopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const target = e.currentTarget;
        if (hasMore && !isLoadingMore && target.scrollTop + target.offsetHeight >= target.scrollHeight - 32) {
            loadMore();
        }
    };
    const options = candidates.map((issue) => {
        const source = issue.sources[0];
        const text = issue.title || issue.description || 'Untitled issue';
        return {
            value: issue.id,
            label: (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0, maxWidth: '100%' }}>
                    <span style={{ flexShrink: 0, lineHeight: 0 }}>
                        {source.type === IssueSourceType.Runway ?
                            <img src={runwayLogo} alt="Runway" width={16} height={16} />
                        :   <IntegrationIcon integrationId={source.integrationType} size={16} />}
                    </span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
                </span>
            ),
        };
    });
    return (
        <Select
            mode="multiple"
            allowClear
            loading={isLoading}
            value={value}
            onChange={onChange}
            options={options}
            filterOption={false}
            onSearch={onSearch}
            onPopupScroll={onPopupScroll}
            placeholder="Select sources from other issues"
            style={{ width: '100%' }}
            notFoundContent={isLoading ? <Spin size="small" /> : 'No standalone sources to add'}
            dropdownRender={(menu) => (
                <>
                    {menu}
                    {isLoadingMore && (
                        <div style={{ textAlign: 'center', padding: 8 }}>
                            <Spin size="small" />
                        </div>
                    )}
                </>
            )}
        />
    );
};
